import {
  IsomorphicFetch,
  type RequestOptions,
  type RequestParams,
} from '@inflearn/datasource-rest';
import { defaultsDeep, has, isEmpty, isNil } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';

import { hasLangQueryString, makeUrlWithLangQueryString } from '@inflearn/i18n-tools/dist/utils';
import {
  NEXT_PUBLIC_ACCOUNT_API_URL,
  NEXT_PUBLIC_COURSE_API_URL,
  NEXT_PUBLIC_DEVOPS_API_URL,
  NEXT_PUBLIC_ENV,
  NEXT_PUBLIC_INFLEARN_HOST,
  NEXT_PUBLIC_INTERNAL_ACCOUNT_API_URL,
  NEXT_PUBLIC_INTERNAL_COURSE_API_URL,
  NEXT_PUBLIC_INTERNAL_MENTORING_API_URL,
  NEXT_PUBLIC_INTERNAL_PAYMENT_API_URL,
  NEXT_PUBLIC_INTERNAL_RALLIT_API_URL,
  NEXT_PUBLIC_INTERNAL_UCC_API_URL,
  NEXT_PUBLIC_MENTORING_API_URL,
  NEXT_PUBLIC_PAYMENT_API_URL,
  NEXT_PUBLIC_RALLIT_API_URL,
  NEXT_PUBLIC_SERVER_MOCK_API_BASE_URL,
  NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_UCC_API_URL,
  NEXT_PUBLIC_USE_MSW,
} from '../appConfig';
import { getCurrentLanguage } from '../src/utils/i18n/getCurrentLanguage';
import { hasWindow } from '../src/utils/window';
import { ResponseStatus } from './@types/schema';
import { ApiError } from './ApiError';
import { CustomAbortError } from './CustomAbortError';
import { isRecordType } from './typegaurd';

const hasErrorCode = (error: ApiError) =>
  error.errorCode !== 'UNKNOWN_ERROR' && !isNil(error.errorCode) && !isEmpty(error.errorCode);

class Fetcher extends IsomorphicFetch {
  protected override afterRequest<T>(
    status: number,
    responseHeaders: Headers,
    requestInfo: RequestParams,
    data: T,
  ): T {
    if (this.isErrorResponse(data)) {
      const error = ApiError.createError(
        // @ts-ignore
        data,
        {
          status,
          url: requestInfo.path,
          handled: true,
          extra: data,
        },
        Number(responseHeaders.get('goose-level')),
      );
      const { level, message } = error.logMessage();

      if (!hasErrorCode(error)) {
        console[level](message);
      }

      throw error;
    }

    return data;
  }

  protected beforeRequest(params: RequestParams): RequestParams {
    const options = {
      ...defaultsDeep(
        {
          headers: {
            'X-Request-Id': uuidv4(), // server-to-server 요청 추적용
            'X-Request-From': 'inflearn-next-fe', // 서비스 확인용
          },
        },
        params.options,
      ),
    } as RequestParams['options'];

    if (params.method === 'GET' && !hasLangQueryString(params.path)) {
      params.path = makeUrlWithLangQueryString(params.path, getCurrentLanguage());
    }

    return {
      ...params,
      options: options,
    };
  }

  override performError(e: unknown) {
    if (e instanceof ApiError && !hasErrorCode(e)) {
      const { level, message } = e.logMessage();
      console[level](message);

      return;
    }

    if (e instanceof CustomAbortError) {
      console.info(
        `[CustomAbortError] ${e.name}: ${e.message} *url: ${e.api} *release: ${
          hasWindow() ? window.__RELEASE__ : ''
        }`,
      );

      return;
    }

    console.error(e);
  }

  private isErrorResponse(response: unknown): response is Error {
    // 앤트맨 에러 핸들링
    if (isRecordType(response) && has(response, 'ok') && !response.ok) {
      return true;
    }

    return (
      isRecordType(response) &&
      has(response, 'data') &&
      has(response, 'statusCode') &&
      has(response, 'message') &&
      response.statusCode !== ResponseStatus.OK
    );
  }
}

const REQUEST_TIME_OUT = 10 * 1000;
const isLocal = NEXT_PUBLIC_ENV === 'localhost';

const getEndpoint = (rewritesURL: string, publicURL?: string, internalURL?: string) => {
  if (!publicURL || !internalURL) {
    throw new Error(`Invalid appConfig URL: ${publicURL}, ${internalURL}`);
  }
  let endpoint = hasWindow() ? publicURL : internalURL;

  if (isLocal) {
    endpoint = rewritesURL;
  }

  return NEXT_PUBLIC_USE_MSW === 'true'
    ? `${NEXT_PUBLIC_SERVER_MOCK_API_BASE_URL}${endpoint}`
    : endpoint;
};

export const uccFetcher = new Fetcher({
  baseUrl: getEndpoint(
    `${NEXT_PUBLIC_SITE_URL}/api/ucc`,
    `${NEXT_PUBLIC_UCC_API_URL}`,
    `${NEXT_PUBLIC_INTERNAL_UCC_API_URL}`,
  ),
  timeout: REQUEST_TIME_OUT,
  credentials: 'include',
});

export const accountFetcher = new Fetcher({
  baseUrl: getEndpoint(
    `${NEXT_PUBLIC_SITE_URL}/api/account`,
    NEXT_PUBLIC_ACCOUNT_API_URL,
    NEXT_PUBLIC_INTERNAL_ACCOUNT_API_URL,
  ),
  timeout: REQUEST_TIME_OUT,
  credentials: 'include',
});

export const courseFetcher = new Fetcher({
  baseUrl: getEndpoint(
    `${NEXT_PUBLIC_SITE_URL}/api/course`,
    NEXT_PUBLIC_COURSE_API_URL,
    NEXT_PUBLIC_INTERNAL_COURSE_API_URL,
  ),
  timeout: REQUEST_TIME_OUT,
  credentials: 'include',
});

export const rallitFetcher = new Fetcher({
  baseUrl: getEndpoint(
    `${NEXT_PUBLIC_RALLIT_API_URL}`,
    NEXT_PUBLIC_RALLIT_API_URL,
    NEXT_PUBLIC_INTERNAL_RALLIT_API_URL,
  ),
  timeout: REQUEST_TIME_OUT,
  credentials: 'include',
});

export const antmanFetcher = new Fetcher({
  baseUrl: `${NEXT_PUBLIC_INFLEARN_HOST}/api`,
  timeout: REQUEST_TIME_OUT,
  credentials: 'include',
});

export const mentoringFetcher = new Fetcher({
  baseUrl: getEndpoint(
    `${NEXT_PUBLIC_SITE_URL}/api/mentoring`,
    NEXT_PUBLIC_MENTORING_API_URL,
    NEXT_PUBLIC_INTERNAL_MENTORING_API_URL,
  ),
  timeout: REQUEST_TIME_OUT,
  credentials: 'include',
});

export const paymentFetcher = new Fetcher({
  baseUrl: getEndpoint(
    `${NEXT_PUBLIC_SITE_URL}/api/payment`,
    NEXT_PUBLIC_PAYMENT_API_URL,
    NEXT_PUBLIC_INTERNAL_PAYMENT_API_URL,
  ),
  timeout: REQUEST_TIME_OUT,
  credentials: 'include',
});

export const devopsFetcher = new Fetcher({
  baseUrl: NEXT_PUBLIC_DEVOPS_API_URL,
  timeout: REQUEST_TIME_OUT * 4,
  credentials: 'include',
});

export type { RequestOptions };
